import React, { useEffect, useState } from "react";
import { db, auth } from "../firebase";
import {
  collection, onSnapshot, query, where,
  doc, updateDoc, addDoc, deleteDoc, getDocs
} from "firebase/firestore";
import { signOut } from "firebase/auth";

// ===== DARK TEAL THEME TOKENS =====
const T = {
  bg:          "#060d1f",
  bgCard:      "rgba(10, 25, 50, 0.80)",
  bgCardSolid: "#0a1628",
  teal:        "#00c9b1",
  tealGlow:    "rgba(0, 201, 177, 0.35)",
  tealBorder:  "rgba(0, 201, 177, 0.22)",
  tealBtn:     "linear-gradient(135deg, #00c9b1, #00e8cc)",
  tealLight:   "rgba(0, 201, 177, 0.1)",
  textWhite:   "#ffffff",
  textMuted:   "rgba(255,255,255,0.55)",
  textLabel:   "rgba(255,255,255,0.75)",
  inputBg:     "rgba(0, 201, 177, 0.07)",
  inputBorder: "rgba(0, 201, 177, 0.25)",
  red:         "linear-gradient(135deg, #ff4d4f, #ff6b6b)",
  green:       "linear-gradient(135deg, #00c9b1, #00e8cc)",
  orange:      "rgba(255, 170, 0, 0.15)",
  font:        "'Poppins', 'Segoe UI', sans-serif",
};

const TABS = ["vendors", "premium", "add_entry"];

// Matches the 10-category structure in Dashboard.js and VendorRegister.js
const CATEGORIES = {
  "Panchayat":           ["Zilla Panchayat", "Taluk Panchayat", "Grama Panchayat"],
  "Agriculture":         ["RSK", "Departments", "Societies", "Seeds", "Fertilizers", "APMC"],
  "Milk":                ["Manmul", "Veterinary", "Feeds", "Pet Pharma"],
  "Health Care":         ["Hospitals", "Diagnostics", "Pharmacy", "Doctors"],
  "Emergency Services":  ["Police", "Ambulance", "Fire"],
  "Banking & Insurance": ["Personal Loan", "Home Loan", "Gold Loan", "Vehicle Loan", "Education Loan", "Other Banking Services", "Life Insurance", "Term Insurance", "Vehicle Insurance", "Crop Insurance", "Business Insurance"],
  "Digital Services":    ["Aadhaar Card", "PAN Card", "Voter ID", "Online Application", "Passport", "Scholarship", "RTC", "Revenue Dept", "Other Services"],
  "District Govt Officers": ["DC Office", "SP Office", "DHO", "DEO", "ZP CEO", "DFO", "Other District Officers"],
  "Taluk Govt Officers": ["Tahsildar", "Taluk Panchayat EO", "BEO", "Agriculture Officer", "Health Inspector", "Other Taluk Officers"],
  "Transportation & Tourism": ["Bus/Train/Flight Booking", "Temple Darshana Booking", "Domestic Tour Packages", "International Tour Packages"],
};

const SUBDISTRICTS = ["Mandya", "Maddur", "Malavalli", "Srirangapatna", "Nagamangala", "Krishnarajapete", "Pandavapura"];

export default function AdminDashboard({ setUser }) {
  const [tab, setTab] = useState("vendors");
  const [vendors, setVendors] = useState([]);
  const [premiumUsers, setPremiumUsers] = useState([]);
  const [manualEntries, setManualEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  const [entryForm, setEntryForm] = useState({
    name: "", phone: "", address: "", description: "",
    website: "", category: "", subcategory: "", subdistrict: ""
  });

  // Real-time listener: pending vendor approvals
  useEffect(() => {
    const q = query(collection(db, "vendors"), where("approvalStatus", "==", "pending"));
    const unsub = onSnapshot(q, (snap) => {
      setVendors(snap.docs.map(d => ({ ...d.data(), id: d.id })));
      setLoading(false);
    });
    return () => unsub();
  }, []);

  // Real-time listener: premium payment requests
  useEffect(() => {
    const q = query(collection(db, "users"), where("paymentStatus", "==", "pending"));
    const unsub = onSnapshot(q, (snap) => {
      setPremiumUsers(snap.docs.map(d => ({ ...d.data(), id: d.id })));
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    if (tab !== "add_entry") return;
    getDocs(collection(db, "manual_entries")).then(snap => {
      setManualEntries(snap.docs.map(d => ({ ...d.data(), id: d.id })));
    });
  }, [tab]);

  // ── Vendor actions ──────────────────────────────────────────
  const approveVendor = async (id) => {
    try {
      await updateDoc(doc(db, "vendors", id), {
        approvalStatus: "approved",
        paymentStatus: "approved",
      });
      alert("Vendor approved ✅");
    } catch (err) {
      alert("❌ Approve failed: " + (err?.message || err));
    }
  };

  const rejectVendor = async (id) => {
    if (!window.confirm("Reject this vendor?")) return;
    try {
      await updateDoc(doc(db, "vendors", id), {
        approvalStatus: "rejected",
        paymentStatus: "rejected",
      });
      alert("Vendor rejected ❌");
    } catch (err) {
      alert("❌ Reject failed: " + (err?.message || err));
    }
  };

  // ── Premium user actions ────────────────────────────────────
  const approvePremium = async (id) => {
    try {
      await updateDoc(doc(db, "users", id), {
        isPremium: true,
        paymentStatus: "approved"
      });
      alert("Premium approved ✅ — user is now unlocked");
    } catch (err) {
      alert("❌ Approve failed: " + (err?.message || err));
    }
  };

  const rejectPremium = async (id) => {
    if (!window.confirm("Reject this premium request?")) return;
    try {
      await updateDoc(doc(db, "users", id), {
        isPremium: false,
        paymentStatus: "rejected"
      });
      alert("Premium rejected ❌");
    } catch (err) {
      alert("❌ Reject failed: " + (err?.message || err));
    }
  };

  // ── Manual entry actions ────────────────────────────────────
  const handleAddEntry = async (e) => {
    e.preventDefault();
    if (!entryForm.name || !entryForm.category || !entryForm.subcategory) {
      alert("Please fill Name, Category, and Subcategory");
      return;
    }
    await addDoc(collection(db, "manual_entries"), { ...entryForm, createdAt: new Date(), type: "manual" });
    alert("Entry added ✅");
    setEntryForm({ name: "", phone: "", address: "", description: "", website: "", category: "", subcategory: "", subdistrict: "" });
    const snap = await getDocs(collection(db, "manual_entries"));
    setManualEntries(snap.docs.map(d => ({ id: d.id, ...d.data() })));
  };

  const deleteEntry = async (id) => {
    if (!window.confirm("Delete this entry?")) return;
    await deleteDoc(doc(db, "manual_entries", id));
    setManualEntries(prev => prev.filter(e => e.id !== id));
  };

  const logout = async () => {
    await signOut(auth);
    localStorage.clear();
    setUser(null);
    window.location.href = "/";
  };

  const tabStyle = (t) => ({
    flex: 1, padding: "13px 0", border: "none", cursor: "pointer",
    fontWeight: 700, fontSize: 12,
    background: tab === t ? "linear-gradient(135deg, #00c9b1, #00e8cc)" : "transparent",
    color: tab === t ? "#060d1f" : "rgba(255,255,255,0.55)",
    borderBottom: tab === t ? "3px solid #00c9b1" : "3px solid rgba(0, 201, 177, 0.1)",
    transition: "all 0.25s",
    fontFamily: T.font,
  });

  const inputStyle = {
    width: "100%", padding: "11px 14px", borderRadius: 12,
    border: `1px solid ${T.inputBorder}`, background: T.inputBg,
    color: T.textWhite, marginTop: 4, boxSizing: "border-box",
    fontFamily: T.font, fontSize: 14, outline: "none",
  };

  const selectStyle = { ...inputStyle, cursor: "pointer" };

  return (
    <div style={{
      fontFamily: T.font,
      background: "radial-gradient(ellipse at 15% 10%, #0a2a40 0%, #060d1f 55%, #030810 100%)",
      minHeight: "100vh", color: T.textWhite,
    }}>

      {/* HEADER */}
      <div style={{
        background: "linear-gradient(135deg, #0a2235 0%, #0d3045 100%)",
        borderBottom: `1px solid ${T.tealBorder}`,
        padding: "16px 20px",
        display: "flex", justifyContent: "space-between", alignItems: "center",
        boxShadow: `0 4px 24px rgba(0,0,0,0.4)`,
      }}>
        <div>
          <h2 style={{ color: T.textWhite, margin: 0, fontSize: 20, letterSpacing: 1 }}>
            Admin Dashboard 🛠
          </h2>
          <p style={{ color: T.textMuted, margin: "2px 0 0", fontSize: 13 }}>Community Connect</p>
        </div>
        <button onClick={logout} style={{
          background: "linear-gradient(135deg, #ff4d4f, #ff6b6b)", color: "#fff",
          border: "none", padding: "9px 18px", borderRadius: 10,
          cursor: "pointer", fontWeight: 700, fontSize: 13, fontFamily: T.font,
        }}>Logout</button>
      </div>

      {/* SUMMARY CARDS */}
      <div style={{ display: "flex", gap: 12, padding: "16px 16px 0" }}>
        {[
          { label: "Pending Vendors",  count: vendors.length,      color: "#ff9500", bg: "rgba(255,149,0,0.12)",     border: "rgba(255,149,0,0.25)" },
          { label: "Premium Requests", count: premiumUsers.length, color: "#00c9b1", bg: "rgba(0,201,177,0.1)",      border: "rgba(0,201,177,0.22)" },
          { label: "Manual Entries",   count: manualEntries.length,color: "#a78bfa", bg: "rgba(167,139,250,0.1)",    border: "rgba(167,139,250,0.22)" },
        ].map((s, i) => (
          <div key={i} style={{
            flex: 1, background: s.bg, border: `1px solid ${s.border}`,
            borderRadius: 16, padding: "14px 10px", textAlign: "center",
            backdropFilter: "blur(12px)", boxShadow: "0 4px 16px rgba(0,0,0,0.25)",
          }}>
            <div style={{ fontSize: 24, fontWeight: 800, color: s.color }}>{s.count}</div>
            <div style={{ fontSize: 11, color: T.textMuted, marginTop: 3, fontWeight: 500 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* TABS */}
      <div style={{ display: "flex", marginTop: 18, borderBottom: `1px solid ${T.tealBorder}` }}>
        <button style={tabStyle("vendors")}   onClick={() => setTab("vendors")}>Pending Vendors</button>
        <button style={tabStyle("premium")}   onClick={() => setTab("premium")}>⭐ Premium</button>
        <button style={tabStyle("add_entry")} onClick={() => setTab("add_entry")}>+ Add Data</button>
      </div>

      <div style={{ padding: 16 }}>

        {/* ===== VENDORS TAB ===== */}
        {tab === "vendors" && (
          <div>
            <h3 style={{ marginBottom: 14, color: T.textWhite, fontSize: 16 }}>
              Pending Vendor Approvals
              <span style={{ marginLeft: 8, background: T.tealLight, border: `1px solid ${T.tealBorder}`, color: T.teal, padding: "2px 10px", borderRadius: 20, fontSize: 12, fontWeight: 700 }}>
                {vendors.length}
              </span>
            </h3>
            {loading ? <p style={{ color: T.textMuted }}>Loading...</p>
              : vendors.length === 0 ? (
                <div style={{ background: T.bgCard, border: `1px solid ${T.tealBorder}`, borderRadius: 18, padding: 32, textAlign: "center", color: T.textMuted }}>
                  ✅ No pending vendor approvals
                </div>
              ) : vendors.map(v => (
                <div key={v.id} style={{
                  background: T.bgCard, border: `1px solid ${T.tealBorder}`,
                  borderRadius: 18, padding: 16, marginBottom: 14,
                  backdropFilter: "blur(12px)", boxShadow: "0 6px 24px rgba(0,0,0,0.3)",
                }}>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 700, fontSize: 16, color: T.textWhite }}>{v.name}</div>
                      <div style={{ color: T.textMuted, fontSize: 13, marginTop: 4 }}>🏢 {v.shopName}</div>
                      <div style={{ color: T.textMuted, fontSize: 13 }}>📞 {v.phone}</div>
                      <div style={{ color: T.textMuted, fontSize: 13 }}>📧 {v.email}</div>
                      <div style={{ color: T.textMuted, fontSize: 13 }}>📂 {v.category} → {v.subcategory}</div>
                      <div style={{ color: T.textMuted, fontSize: 13 }}>🗺 {v.subdistrict}</div>
                      <div style={{ color: T.textMuted, fontSize: 13 }}>📍 {v.address}</div>
                      <span style={{
                        background: "rgba(255,170,0,0.15)", color: "#ffaa00",
                        border: "1px solid rgba(255,170,0,0.25)",
                        padding: "3px 10px", borderRadius: 8, fontSize: 11,
                        marginTop: 8, display: "inline-block", fontWeight: 600,
                      }}>💳 Payment: {v.paymentStatus}</span>
                    </div>
                    {v.imageUrl && (
                      <img src={v.imageUrl} alt="" style={{
                        width: 64, height: 64, borderRadius: "50%",
                        objectFit: "cover", border: `2px solid ${T.tealBorder}`,
                      }} />
                    )}
                  </div>
                  <div style={{ display: "flex", gap: 10, marginTop: 14 }}>
                    <button onClick={() => approveVendor(v.id)} style={{
                      flex: 1, background: T.green, color: "#060d1f", border: "none",
                      padding: "11px", borderRadius: 12, fontWeight: 700, cursor: "pointer",
                      fontFamily: T.font, fontSize: 13,
                    }}>✅ Approve</button>
                    <button onClick={() => rejectVendor(v.id)} style={{
                      flex: 1, background: T.red, color: "#fff", border: "none",
                      padding: "11px", borderRadius: 12, fontWeight: 700, cursor: "pointer",
                      fontFamily: T.font, fontSize: 13,
                    }}>❌ Reject</button>
                  </div>
                </div>
              ))}
          </div>
        )}

        {/* ===== PREMIUM REQUESTS TAB ===== */}
        {tab === "premium" && (
          <div>
            <h3 style={{ marginBottom: 14, color: T.textWhite, fontSize: 16 }}>
              Premium Subscription Requests
              <span style={{ marginLeft: 8, background: T.tealLight, border: `1px solid ${T.tealBorder}`, color: T.teal, padding: "2px 10px", borderRadius: 20, fontSize: 12, fontWeight: 700 }}>
                {premiumUsers.length}
              </span>
            </h3>
            {premiumUsers.length === 0 ? (
              <div style={{ background: T.bgCard, border: `1px solid ${T.tealBorder}`, borderRadius: 18, padding: 32, textAlign: "center", color: T.textMuted }}>
                ✅ No pending premium requests
              </div>
            ) : premiumUsers.map(u => (
              <div key={u.id} style={{
                background: T.bgCard, border: `1px solid ${T.tealBorder}`,
                borderRadius: 18, padding: 16, marginBottom: 14,
                backdropFilter: "blur(12px)", boxShadow: "0 6px 24px rgba(0,0,0,0.3)",
              }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 15, color: T.textWhite }}>{u.name || "User"}</div>
                    <div style={{ color: T.textMuted, fontSize: 13, marginTop: 4 }}>📧 {u.email || "N/A"}</div>
                    <div style={{ color: T.textMuted, fontSize: 13 }}>📞 {u.phone || "N/A"}</div>
                    {u.subdistrict && <div style={{ color: T.textMuted, fontSize: 13 }}>🗺 {u.subdistrict}</div>}
                    <span style={{
                      background: "rgba(255,170,0,0.15)", color: "#ffaa00",
                      border: "1px solid rgba(255,170,0,0.25)",
                      padding: "3px 10px", borderRadius: 8, fontSize: 11,
                      display: "inline-block", marginTop: 8, fontWeight: 600,
                    }}>⭐ ₹99/month — Pending</span>
                  </div>
                  {u.photoURL && (
                    <img src={u.photoURL} alt="" style={{
                      width: 52, height: 52, borderRadius: "50%",
                      objectFit: "cover", border: `2px solid ${T.tealBorder}`,
                    }} />
                  )}
                </div>
                <div style={{ display: "flex", gap: 10, marginTop: 14 }}>
                  <button onClick={() => approvePremium(u.id)} style={{
                    flex: 1, background: T.green, color: "#060d1f", border: "none",
                    padding: "11px", borderRadius: 12, fontWeight: 700, cursor: "pointer",
                    fontFamily: T.font, fontSize: 13,
                  }}>✅ Approve Premium</button>
                  <button onClick={() => rejectPremium(u.id)} style={{
                    flex: 1, background: T.red, color: "#fff", border: "none",
                    padding: "11px", borderRadius: 12, fontWeight: 700, cursor: "pointer",
                    fontFamily: T.font, fontSize: 13,
                  }}>❌ Reject</button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ===== ADD ENTRY TAB ===== */}
        {tab === "add_entry" && (
          <div>
            <h3 style={{ marginBottom: 8, color: T.textWhite, fontSize: 16 }}>Add Manual Entry</h3>
            <p style={{ color: T.textMuted, fontSize: 13, marginBottom: 16 }}>
              Add department contacts, government offices, or any service info for the User Dashboard.
            </p>

            <form onSubmit={handleAddEntry} style={{
              background: T.bgCard, border: `1px solid ${T.tealBorder}`,
              borderRadius: 18, padding: 18, marginBottom: 24,
              backdropFilter: "blur(12px)", boxShadow: "0 6px 24px rgba(0,0,0,0.3)",
            }}>
              {[
                { key: "name",        label: "Name / Title *",       placeholder: "e.g. Mandya Police Station" },
                { key: "phone",       label: "Phone Number",          placeholder: "e.g. 9876543210" },
                { key: "address",     label: "Address",               placeholder: "Full address" },
                { key: "description", label: "Description",           placeholder: "Brief info about this entry" },
                { key: "website",     label: "Website (optional)",    placeholder: "https://..." }
              ].map(f => (
                <div key={f.key} style={{ marginBottom: 14 }}>
                  <label style={{ fontSize: 13, fontWeight: 600, color: T.teal }}>{f.label}</label>
                  <input
                    value={entryForm[f.key]}
                    onChange={e => setEntryForm({ ...entryForm, [f.key]: e.target.value })}
                    placeholder={f.placeholder}
                    style={inputStyle}
                  />
                </div>
              ))}

              <div style={{ marginBottom: 14 }}>
                <label style={{ fontSize: 13, fontWeight: 600, color: T.teal }}>Category *</label>
                <select
                  value={entryForm.category}
                  onChange={e => setEntryForm({ ...entryForm, category: e.target.value, subcategory: "" })}
                  style={selectStyle}
                >
                  <option value="">Select Category</option>
                  {Object.keys(CATEGORIES).map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>

              {entryForm.category && (
                <div style={{ marginBottom: 14 }}>
                  <label style={{ fontSize: 13, fontWeight: 600, color: T.teal }}>Subcategory *</label>
                  <select
                    value={entryForm.subcategory}
                    onChange={e => setEntryForm({ ...entryForm, subcategory: e.target.value })}
                    style={selectStyle}
                  >
                    <option value="">Select Subcategory</option>
                    {CATEGORIES[entryForm.category].map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              )}

              <div style={{ marginBottom: 18 }}>
                <label style={{ fontSize: 13, fontWeight: 600, color: T.teal }}>Sub District (optional)</label>
                <select
                  value={entryForm.subdistrict}
                  onChange={e => setEntryForm({ ...entryForm, subdistrict: e.target.value })}
                  style={selectStyle}
                >
                  <option value="">All Districts</option>
                  {SUBDISTRICTS.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>

              <button type="submit" style={{
                width: "100%", padding: "14px", background: T.green,
                color: "#060d1f", border: "none", borderRadius: 14,
                fontWeight: 700, fontSize: 15, cursor: "pointer", fontFamily: T.font,
              }}>+ Add Entry</button>
            </form>

            <h3 style={{ marginBottom: 12, color: T.textWhite, fontSize: 16 }}>
              Existing Manual Entries
              <span style={{ marginLeft: 8, background: T.tealLight, border: `1px solid ${T.tealBorder}`, color: T.teal, padding: "2px 10px", borderRadius: 20, fontSize: 12, fontWeight: 700 }}>
                {manualEntries.length}
              </span>
            </h3>
            {manualEntries.length === 0 ? (
              <div style={{ background: T.bgCard, border: `1px solid ${T.tealBorder}`, borderRadius: 18, padding: 26, textAlign: "center", color: T.textMuted }}>
                No manual entries yet.
              </div>
            ) : manualEntries.map(e => (
              <div key={e.id} style={{
                background: T.bgCard, border: `1px solid ${T.tealBorder}`,
                borderRadius: 16, padding: 14, marginBottom: 10,
                backdropFilter: "blur(12px)",
                display: "flex", justifyContent: "space-between", alignItems: "flex-start",
                boxShadow: "0 4px 16px rgba(0,0,0,0.25)",
              }}>
                <div>
                  <div style={{ fontWeight: 700, color: T.textWhite }}>{e.name}</div>
                  <div style={{ color: T.teal, fontSize: 12, marginTop: 2 }}>{e.category} → {e.subcategory}</div>
                  {e.phone    && <div style={{ color: T.textMuted, fontSize: 13, marginTop: 2 }}>📞 {e.phone}</div>}
                  {e.address  && <div style={{ color: T.textMuted, fontSize: 13 }}>📍 {e.address}</div>}
                  {e.subdistrict && <div style={{ color: T.textMuted, fontSize: 12 }}>🗺 {e.subdistrict}</div>}
                </div>
                <button onClick={() => deleteEntry(e.id)} style={{
                  background: "rgba(255,77,79,0.12)", color: "#ff6b6b",
                  border: "1px solid rgba(255,77,79,0.3)",
                  padding: "7px 14px", borderRadius: 10, cursor: "pointer",
                  fontSize: 12, fontWeight: 700, fontFamily: T.font, whiteSpace: "nowrap",
                }}>Delete</button>
              </div>
            ))}
          </div>
        )}

      </div>
    </div>
  );
}
